import {
  VehicleStatus,
  VehicleValuationProvider,
  VehicleValuationSearchBodyFuelType,
  VehicleValuationSearchBodyVehicleType,
  VehicleValuationStatus,
  VehicleValuationType,
} from "@/api-schema-enums";
import { VehicleValuationSearchBody } from "@/common/api/vehicle-valuation/use-vehicle-valuation-search.query";
import { inputStringToDate } from "@/common/helpers/date-input";
import { formatDateToApi } from "@/common/helpers/format-date";
import { DateFormat } from "@/store/user-config-store";

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function toIsoDateOrUndefined(value: string | undefined, dateFormat: DateFormat): string | undefined {
  if (!value) {
    return undefined;
  }

  if (ISO_DATE_REGEX.test(value)) {
    return value;
  }

  const parsedDate = inputStringToDate(value, { dateFormat });
  if (!parsedDate) {
    return undefined;
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function buildVehicleValuationSearchBody({
  filterValues,
  canManageVehicleValuation,
  dateFormat,
  nowDate = new Date(),
}: {
  filterValues: Record<string, string[]>;
  canManageVehicleValuation: boolean;
  dateFormat: DateFormat;
  nowDate?: Date;
}): VehicleValuationSearchBody {
  const registrationDateFrom = toIsoDateOrUndefined(filterValues.registrationDate?.[0], dateFormat);
  const explicitRegistrationDateTo = toIsoDateOrUndefined(filterValues.registrationDate?.[1], dateFormat);
  const registrationDateTo =
    explicitRegistrationDateTo ?? (registrationDateFrom ? formatDateToApi(nowDate) : undefined);

  return {
    ...(filterValues.vehicles?.length > 0 && { vehicle: filterValues.vehicles }),
    ...(filterValues.brands?.length > 0 && { brand: filterValues.brands }),
    ...(filterValues.modelFamilies?.length > 0 && { modelFamily: filterValues.modelFamilies }),
    ...(filterValues.modelGenerations?.length > 0 && { modelGeneration: filterValues.modelGenerations }),
    ...(filterValues.statuses?.length > 0 && { status: filterValues.statuses as VehicleValuationStatus[] }),
    ...(filterValues.providers?.length > 0 && { provider: filterValues.providers as VehicleValuationProvider[] }),
    ...(filterValues.vehicleStatus?.length > 0 && { vehicleStatus: filterValues.vehicleStatus as VehicleStatus[] }),
    ...(filterValues.requester?.length > 0 && { requester: filterValues.requester }),
    ...(filterValues.requesterCompany?.length > 0 && { requesterCompany: filterValues.requesterCompany }),
    ...(filterValues.requesterBranch?.length > 0 && { requesterBranch: filterValues.requesterBranch }),
    ...(filterValues.confirmedBy?.length > 0 && { confirmedBy: filterValues.confirmedBy }),
    ...(filterValues.valuationType?.length > 0 && {
      valuationType: filterValues.valuationType as VehicleValuationType[],
    }),
    ...(filterValues.vatDeductible?.length === 1 && {
      vatDeductible: filterValues.vatDeductible[0] === "true",
    }),
    ...(filterValues.fuelType?.length > 0 && {
      fuelType: filterValues.fuelType as VehicleValuationSearchBodyFuelType[],
    }),
    ...(filterValues.vehicleType?.length > 0 && {
      vehicleType: filterValues.vehicleType as VehicleValuationSearchBodyVehicleType[],
    }),
    ...(registrationDateFrom && { registrationDateFrom }),
    ...(registrationDateTo && { registrationDateTo }),
    ...(filterValues.mileage?.[0] && { mileageMin: Number.parseInt(filterValues.mileage[0], 10) }),
    ...(filterValues.mileage?.[1] && { mileageMax: Number.parseInt(filterValues.mileage[1], 10) }),
    ...(filterValues.powerKw?.[0] && { powerKwMin: Number.parseInt(filterValues.powerKw[0], 10) }),
    ...(filterValues.powerKw?.[1] && { powerKwMax: Number.parseInt(filterValues.powerKw[1], 10) }),
    ...(filterValues.powerPs?.[0] && { powerPsMin: Number.parseInt(filterValues.powerPs[0], 10) }),
    ...(filterValues.powerPs?.[1] && { powerPsMax: Number.parseInt(filterValues.powerPs[1], 10) }),
    ...(filterValues.createdAt?.[0] && {
      createdAtFrom: toIsoDateOrUndefined(filterValues.createdAt[0], dateFormat),
    }),
    ...(filterValues.createdAt?.[1] && {
      createdAtTo: toIsoDateOrUndefined(filterValues.createdAt[1], dateFormat),
    }),
    ...(canManageVehicleValuation &&
      filterValues.tradeGross?.[0] && {
        tradeGrossMin: Number(filterValues.tradeGross[0]),
      }),
    ...(canManageVehicleValuation &&
      filterValues.tradeGross?.[1] && {
        tradeGrossMax: Number(filterValues.tradeGross[1]),
      }),
    ...(canManageVehicleValuation &&
      filterValues.salesGross?.[0] && {
        salesGrossMin: Number(filterValues.salesGross[0]),
      }),
    ...(canManageVehicleValuation &&
      filterValues.salesGross?.[1] && {
        salesGrossMax: Number(filterValues.salesGross[1]),
      }),
    ...(canManageVehicleValuation &&
      filterValues.marginGross?.[0] && {
        marginGrossMin: Number(filterValues.marginGross[0]),
      }),
    ...(canManageVehicleValuation &&
      filterValues.marginGross?.[1] && {
        marginGrossMax: Number(filterValues.marginGross[1]),
      }),
  };
}
